import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop, Fade, Box, Paper, Typography, Divider, CircularProgress
} from "@material-ui/core";
import { Line } from "react-chartjs-2";
import BranchSelect from "../../Components/BranchSelect";
import DateRangeBar from "../../Components/DateRangeBar";
import ExportButtons from "../../Components/ExportButtons";
import { Api } from "../../api/client";

// ✅ IMPORTANTE EN CHART.JS v3 (igual que tu DashboardGerencial)
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend
} from "chart.js";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend
);

function todayISO(){
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function daysAgoISO(n){
  const d = new Date();
  d.setDate(d.getDate()-n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

/**
 * Normaliza lo que venga desde Api.salesByDay(params)
 * Soporta:
 * - [ {fecha, ventas}, ... ]
 * - { status:'ok', data: [ {fecha, ventas}, ... ] }
 * - { labels:[...], values:[...] }
 * - { data:{ labels:[...], values:[...] } }
 */
function normalizeSalesByDayResponse(r){
  if (!r) return [];

  // Si viene {status,data} (CodeIgniter típico), "baja" a data
  if (!Array.isArray(r) && r.status && r.data !== undefined) {
    r = r.data;
  }

  // Caso A: array de objetos
  if (Array.isArray(r)) {
    return r.map(x => ({
      fecha: x.fecha ?? x.label ?? "",
      ventas: Number(x.ventas ?? x.value ?? 0)
    })).filter(x => x.fecha);
  }

  // Caso B: {labels, values}
  const labels1 = Array.isArray(r.labels) ? r.labels : null;
  const values1 = Array.isArray(r.values) ? r.values : null;
  if (labels1 && values1) {
    return labels1.map((l, i) => ({
      fecha: l,
      ventas: Number(values1[i] ?? 0)
    })).filter(x => x.fecha);
  }

  // Caso C: {data:{labels,values}}
  const labels2 = Array.isArray(r.data?.labels) ? r.data.labels : null;
  const values2 = Array.isArray(r.data?.values) ? r.data.values : null;
  if (labels2 && values2) {
    return labels2.map((l, i) => ({
      fecha: l,
      ventas: Number(values2[i] ?? 0)
    })).filter(x => x.fecha);
  }

  return [];
}

export default function VentasPeriodo(){
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [range, setRange] = useState({ from: daysAgoISO(7), to: todayISO() });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [debug, setDebug] = useState({ ok: true, msg: "" });

  const params = useMemo(
    () => ({ from: range.from, to: range.to, branch: branchId }),
    [range.from, range.to, branchId]
  );

  useEffect(() => {
    Api.branches()
      .then(res => setBranches(res.data))
      .catch(() => setBranches([{ id: "", nombre: "Todas" }]));
  }, []);

  useEffect(() => {
    let alive = true;

    async function load(){
      setLoading(true);
      setDebug({ ok: true, msg: "" });

      try {
        // 🔥 aquí pega a tu API real (mismo método que el dashboard)
        const r = await Api.salesByDay(params);

        const normalized = normalizeSalesByDayResponse(r);

        if (!alive) return;

        setRows(normalized);

        if (!normalized.length) {
          setDebug({
            ok: false,
            msg: "El API respondió, pero no hay registros para ese rango/sucursal (o el response no coincide con el formato esperado)."
          });
        }
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setRows([]);
        setDebug({
          ok: false,
          msg: "Error consultando el API. Revisa Network/Console."
        });
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => { alive = false; };
  }, [params.from, params.to, params.branch]);

  const chart = useMemo(() => ({
    data: {
      labels: rows.map(x => x.fecha),
      datasets: [{
        label: "Ventas USD",
        data: rows.map(x => Number(x.ventas || 0))
      }]
    },
    options: {
      responsive: true,
      plugins: { legend: { display: true } }
    }
  }), [rows]);

  return (
    <>
      <Backdrop open={loading} style={{ zIndex: 2000, color: "#fff", backdropFilter: "blur(3px)" }}>
        <Fade in={loading}>
          <Paper style={{ padding: 30, borderRadius: 14, textAlign: "center" }}>
            <CircularProgress />
            <Typography style={{ marginTop: 12, fontWeight: 900 }}>Cargando ventas…</Typography>
            <Typography variant="body2" style={{ opacity: 0.75, marginTop: 6 }}>
              Generando reporte por período
            </Typography>
          </Paper>
        </Fade>
      </Backdrop>

      <Paper style={{ padding: 18, borderRadius: 14 }}>
        <Typography variant="h6" style={{ fontWeight: 900 }}>Ventas por período</Typography>
        <Divider style={{ margin: "12px 0" }} />

        <Box display="flex" gridGap={12} flexWrap="wrap">
          <BranchSelect value={branchId} options={branches} onChange={setBranchId} />
          <DateRangeBar from={range.from} to={range.to} onChange={setRange} />
          <ExportButtons
            filename={`ventas_periodo_${params.from}_a_${params.to}.csv`}
            rows={rows}
          />
        </Box>

        {/* ✅ Debug mínimo para que veas SI llega data */}
        <Box mt={2} style={{ fontSize: 12, opacity: 0.85 }}>
          <b>Registros:</b> {rows.length}
          {rows.length ? (
            <span style={{ marginLeft: 10 }}>
              | <b>Primero:</b> {rows[0].fecha} → {rows[0].ventas}
            </span>
          ) : null}
        </Box>

        <Box mt={3}>
          {rows.length ? (
            <Line data={chart.data} options={chart.options} />
          ) : (
            <Typography variant="body2" style={{ opacity: 0.7, marginTop: 10 }}>
              {debug.ok ? "No hay datos para el rango seleccionado." : debug.msg}
            </Typography>
          )}
        </Box>
      </Paper>
    </>
  );
}